import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Slider,
  Switch,
  Typography,
} from "@mui/material";
import styled from "styled-components";
import { isMobile } from "react-device-detect";
import TokenIcon from "@mui/icons-material/Token";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { usePallet } from "../../../contexts/PalletContext";
import { useAuthorization } from "../../../contexts/AuthorizationContext";
import { useAlert } from "../../../contexts/AlertContext";
import PieChartRelatorioConsumoIa from "../../relatorios/_components/PieChartRelatorioConsumoIa";
import ConsumoService, {
  IConsumoIAResponse,
  TAgenteConsumoIA,
} from "../../../services/ConsumoService";

// =====================================
// Consumo IA — dados reais do mês corrente, SÓ em legisdots.
// A conversão (custo, câmbio, margem, pacote) acontece inteira no backend; nada
// disso chega aqui. Layout = o aprovado pelo cliente (mock anterior), com dado real.
// =====================================

// Agentes na ordem do layout: rótulo exibido, cor e flag de processamento que o switch grava.
const AGENTES: Record<TAgenteConsumoIA, { label: string; color: string; codigo: string }> = {
  jabuti: { label: "Analista Detector de Jabutis (Federal)", color: "#4B1196", codigo: "HAB_JABUTI" },
  mercados: { label: "Analista de Mercados e Interesses (Federal)", color: "#7A38C9", codigo: "HAB_MERCADOS" },
  sumarizacao: { label: "Analista Sumarizador (Federal)", color: "#B084DE", codigo: "HAB_SUMARIZACAO" },
  perfil: { label: "Analista de Interlocutor (Federal)", color: "#6366F1", codigo: "HAB_PERFIL" },
  smart_talk: { label: "Smart Talk", color: "#9B5FD9", codigo: "HAB_SMART_TALK" },
  nota_tecnica: { label: "Assessor de Nota Técnica", color: "#DBCFEA", codigo: "HAB_NOTA_TEC_COLAB" },
};

// Chave camelCase que a API de parâmetros devolve para cada flag (getHabilitacoes).
const FLAG_CAMEL: Record<TAgenteConsumoIA, string> = {
  jabuti: "habJabuti",
  mercados: "habMercados",
  sumarizacao: "habSumarizacao",
  perfil: "habPerfil",
  smart_talk: "habSmartTalk",
  nota_tecnica: "habNotaTecColab",
};

const PERMISSAO_MASTER = 1;

// =====================================
// Helpers de exibição
// =====================================
// Totais do mês: 2 casas.
function fmtTotal(n: number) {
  return Number(n || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Média por unidade: 4 casas; até 6 quando menor que 0,0001 (nunca aparece "0,0000" com consumo real).
function fmtMedia(n: number) {
  const v = Number(n || 0);
  const max = v > 0 && v < 0.0001 ? 6 : 4;
  return v.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: max });
}

function fmtPercent(n: number) {
  return Number(n || 0).toLocaleString("pt-BR", { maximumFractionDigits: 2 });
}

// =====================================
// Styled
// =====================================
const Container = styled(Grid)`
  padding: 20px 16px;
  background: #fafafa;
`;

const KPICard = styled(Paper)`
  padding: 18px;
  border-radius: 10px;
  display: flex;
  gap: 14px;
  height: 100%;
  box-sizing: border-box;
`;

const SectionPaper = styled(Paper)`
  padding: 20px;
  border-radius: 10px;
  margin-top: 16px;
`;

const LegendRow = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 4px;
  border-bottom: 1px solid #f0f0f0;
`;

const ColorDot = styled.span<{ color: string }>`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 3px;
  background: ${(p) => p.color};
  margin-right: 10px;
`;

// =====================================
// Componente
// =====================================
const GerUsuariosTabConsumoIa = (_props: { id_user: number }) => {
  const { pallet } = usePallet();
  const { user } = useAuthorization();
  const { NewAlert } = useAlert();

  const corPrimaria = pallet?.backgroundColorPrimary || "#3B0675";
  const corTexto = pallet?.textColorQuaternary || "#1E0736";

  // Ligar/desligar agente e solicitar legisdots são do administrador (master) — mesmas
  // regras das rotas da API. Os demais perfis veem o painel, sem ação.
  const ehMaster = user?.id_permissao === PERMISSAO_MASTER;

  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(false);
  const [consumo, setConsumo] = useState<IConsumoIAResponse | undefined>(undefined);
  const [salvandoAgente, setSalvandoAgente] = useState<TAgenteConsumoIA | null>(null);

  const carregar = useCallback(() => {
    setLoading(true);
    ConsumoService.GetConsumoIA()
      .then((res) => {
        setConsumo(res);
        setErro(!res);
      })
      .catch(() => setErro(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const agentes = useMemo(
    () =>
      (consumo?.agentes || [])
        .filter((a) => AGENTES[a.agente])
        .map((a) => ({ ...a, ...AGENTES[a.agente] })),
    [consumo]
  );

  const contratados = consumo?.contratados ?? 0;
  const consumidos = consumo?.consumidos ?? 0;
  const faltam = consumo?.faltam ?? 0;
  const percent = consumo?.percentual ?? 0;
  const projecao = consumo?.projecao;
  const limiteAtingido = !!consumo?.limite_atingido;

  // Agente desligado CONTINUA no gráfico e no total: o consumo dele no mês já aconteceu.
  const totalConsumoMes = agentes.reduce((acc, a) => acc + a.legisdots, 0);
  const pieLabels = agentes
    .filter((a) => a.legisdots > 0)
    .map((a) => ({ key: a.label, data: a.legisdots, color: a.color, url: "#" }));

  const toggleAgente = async (agente: TAgenteConsumoIA, ativo: boolean) => {
    setSalvandoAgente(agente);
    const res = await ConsumoService.AlterarAgenteIA(AGENTES[agente].codigo, ativo);
    setSalvandoAgente(null);
    if (!res.ok) {
      NewAlert("error", "Não foi possível alterar o agente.");
      carregar();
      return;
    }
    // A API devolve as habilitações já gravadas: reflete sem esperar novo GET.
    const hab = res.data || {};
    setConsumo((prev) =>
      prev
        ? { ...prev, agentes: prev.agentes.map((a) => ({ ...a, ativo: !!hab[FLAG_CAMEL[a.agente]] })) }
        : prev
    );
  };

  // ---- Solicitação de legisdots (popup) ----
  // Escala do slider = a franquia mensal do contrato (1 legisdot por passo). O padrão é a
  // contratação recomendada pela projeção; sem recomendação, 10% da franquia.
  const sliderMax = Math.max(1, Math.ceil(consumo?.franquia || 0));
  const quantidadeSugerida = () => {
    const recomendada = Math.ceil(projecao?.indicacao_contratar || 0);
    const base = recomendada > 0 ? recomendada : Math.ceil(sliderMax * 0.1);
    return Math.min(sliderMax, Math.max(1, base));
  };

  const [openContratar, setOpenContratar] = useState(false);
  const [aceite, setAceite] = useState(false);
  const [confirmacaoOk, setConfirmacaoOk] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [quantidade, setQuantidade] = useState<number>(1);
  const [dataContratacao, setDataContratacao] = useState<Date>(new Date());

  const handleAbrirContratacao = () => {
    setQuantidade(quantidadeSugerida());
    setDataContratacao(new Date());
    setAceite(false);
    setConfirmacaoOk(false);
    setOpenContratar(true);
  };

  const handleConfirmarContratacao = async () => {
    setEnviando(true);
    const res = await ConsumoService.SolicitarLegisdots(quantidade);
    setEnviando(false);
    if (!res.ok) {
      NewAlert("error", res.mensagem || "Não foi possível registrar a solicitação.");
      return;
    }
    if (res.data?.solicitado_em) setDataContratacao(new Date(res.data.solicitado_em));
    setConfirmacaoOk(true);
    carregar();
    setTimeout(() => {
      setOpenContratar(false);
    }, 2500);
  };

  if (loading && !consumo) {
    return (
      <Container container justifyContent="center" alignItems="center" style={{ minHeight: 240 }}>
        <CircularProgress sx={{ color: corPrimaria }} />
      </Container>
    );
  }

  if (erro && !consumo) {
    return (
      <Container container>
        <Alert severity="error" sx={{ width: "100%", fontFamily: pallet?.general?.fontFamily }}>
          Não foi possível carregar o consumo.
        </Alert>
      </Container>
    );
  }

  return (
    <Container container>
      {/* WIDGETS LADO A LADO */}
      <Grid container spacing={2}>
        {/* Widget 1 - Legisdots Consumidos */}
        <Grid item xs={12} md={6}>
          <KPICard elevation={1}>
            <Avatar sx={{ bgcolor: corPrimaria }}>
              <TokenIcon />
            </Avatar>
            <Grid sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#7a7a7a",
                  fontFamily: pallet?.general?.fontFamily,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Legisdots consumidos · mês atual
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  color: corTexto,
                  fontFamily: pallet?.general?.fontFamily,
                  mt: 0.5,
                }}
              >
                {fmtTotal(consumidos)}
                <Typography
                  component="span"
                  sx={{ fontSize: 13, color: "#7a7a7a", ml: 1, fontWeight: 500 }}
                >
                  / {fmtTotal(contratados)}
                </Typography>
              </Typography>

              <Grid sx={{ mt: 1.5 }}>
                <LinearProgress
                  variant="buffer"
                  value={Math.min(100, percent)}
                  valueBuffer={Math.min(100, percent + 6)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: "#E8E8E8",
                    "& .MuiLinearProgress-bar": { backgroundColor: corPrimaria },
                    "& .MuiLinearProgress-dashed": { backgroundImage: "none" },
                  }}
                />
                <Typography sx={{ fontSize: 11, color: "#7a7a7a", mt: 0.5 }}>
                  {fmtPercent(percent)}% consumidos
                </Typography>
              </Grid>

              <Alert
                severity="warning"
                icon={<WarningAmberIcon fontSize="inherit" />}
                sx={{
                  mt: 1.5,
                  py: 0.5,
                  fontSize: 12,
                  alignItems: "center",
                  background: "rgba(255, 167, 38, 0.08)",
                  border: "1px solid rgba(255, 167, 38, 0.25)",
                  color: "#8a5a00",
                }}
              >
                Faltam <b>{fmtTotal(faltam)}</b> legisdots para
                concluir o processamento do mês.
              </Alert>
            </Grid>
          </KPICard>
        </Grid>

        {/* Widget 2 - Contratar Legisdots */}
        <Grid item xs={12} md={6}>
          <KPICard elevation={1}>
            <Avatar sx={{ bgcolor: pallet?.backgroundColorQuintenary || "#dbcfea", color: corPrimaria }}>
              <ShoppingCartIcon />
            </Avatar>
            <Grid sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontSize: 12,
                  color: "#7a7a7a",
                  fontFamily: pallet?.general?.fontFamily,
                  textTransform: "uppercase",
                  letterSpacing: 0.4,
                }}
              >
                Contratação recomendada
              </Typography>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 22,
                  color: corTexto,
                  fontFamily: pallet?.general?.fontFamily,
                  mt: 0.5,
                }}
              >
                {fmtTotal(projecao?.indicacao_contratar || 0)}
                <Typography component="span" sx={{ fontSize: 13, color: "#7a7a7a", ml: 1, fontWeight: 500 }}>
                  legisdots
                </Typography>
              </Typography>

              <Typography sx={{ fontSize: 11, color: "#7a7a7a", mt: 0.5 }}>
                Projeção até o fim do mês:{" "}
                <b>{fmtTotal(projecao?.consumo_projetado || 0)}</b> legisdots
              </Typography>

              <Button
                fullWidth
                variant="contained"
                onClick={handleAbrirContratacao}
                disabled={!ehMaster}
                sx={{
                  mt: 1.5,
                  background: corPrimaria,
                  textTransform: "none",
                  fontFamily: pallet?.general?.fontFamily,
                  fontWeight: 600,
                  fontSize: 13,
                  "&:hover": { background: pallet?.textColorTertiary || "#4B1196" },
                }}
              >
                Contratar Legisdots
              </Button>
            </Grid>
          </KPICard>
        </Grid>
      </Grid>

      {/* PIZZA + LEGENDA COM TOGGLE */}
      <SectionPaper sx={{ width: "100%" }}>
        <Typography
          sx={{
            fontWeight: 700,
            color: corTexto,
            fontFamily: pallet?.general?.fontFamily,
            fontSize: 16,
            mb: 2,
          }}
        >
          Consumo por Tipo
        </Typography>

        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            {pieLabels.length > 0 ? (
              <PieChartRelatorioConsumoIa
                title="Consumo por Tipo"
                dtIni={new Date(new Date().getFullYear(), new Date().getMonth(), 1)}
                dtFin={new Date()}
                labels={pieLabels}
              />
            ) : (
              <Grid
                container
                alignItems="center"
                justifyContent="center"
                style={{ height: 220, border: "1px dashed #e0e0e0", borderRadius: 7 }}
              >
                <Typography sx={{ color: "#7a7a7a" }}>
                  Sem consumo no mês até o momento.
                </Typography>
              </Grid>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              sx={{
                fontSize: 12,
                color: "#7a7a7a",
                textTransform: "uppercase",
                letterSpacing: 0.4,
                mb: 1,
                fontFamily: pallet?.general?.fontFamily,
              }}
            >
              Agentes · média de legisdots por unidade processada
            </Typography>

            {agentes.map((a) => (
              <LegendRow key={a.agente} container>
                <Grid item display="flex" alignItems="center" sx={{ flex: 1 }}>
                  <ColorDot color={a.color} />
                  <Typography
                    sx={{
                      fontFamily: pallet?.general?.fontFamily,
                      fontSize: 13,
                      color: a.ativo ? corTexto : "#aaa",
                      fontWeight: 600,
                    }}
                  >
                    {a.label}
                  </Typography>
                  {!a.ativo && (
                    <Typography
                      component="span"
                      sx={{
                        ml: 1,
                        px: 0.75,
                        fontSize: 10,
                        lineHeight: "16px",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        color: "#999",
                        textTransform: "uppercase",
                        letterSpacing: 0.4,
                        fontFamily: pallet?.general?.fontFamily,
                      }}
                    >
                      desligado
                    </Typography>
                  )}
                </Grid>
                <Grid item display="flex" alignItems="center" sx={{ gap: 1.5 }}>
                  <Typography
                    sx={{
                      fontFamily: pallet?.general?.fontFamily,
                      fontSize: 13,
                      color: a.ativo ? "#555" : "#aaa",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {fmtMedia(a.media_por_unidade)}
                  </Typography>
                  <Switch
                    size="small"
                    checked={a.ativo}
                    disabled={!ehMaster || limiteAtingido || salvandoAgente !== null}
                    onChange={(e) => toggleAgente(a.agente, e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: corPrimaria },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        backgroundColor: corPrimaria,
                      },
                    }}
                  />
                </Grid>
              </LegendRow>
            ))}

            <Grid container justifyContent="space-between" sx={{ pt: 1.5 }}>
              <Typography sx={{ fontSize: 12, color: "#7a7a7a" }}>
                Total ativo no mês
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: corTexto }}>
                {fmtTotal(totalConsumoMes)} legisdots
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </SectionPaper>

      {/* DIALOG DE CONTRATAÇÃO */}
      <Dialog
        open={openContratar}
        onClose={() => !enviando && setOpenContratar(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "10px" } }}
      >
        <DialogTitle
          sx={{
            background: pallet?.backgroundColorTertiary || "#F9F9F9",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontFamily: pallet?.general?.fontFamily,
            color: corTexto,
            fontWeight: 700,
            fontSize: 18,
          }}
        >
          <Grid display="flex" alignItems="center" sx={{ gap: 1 }}>
            <ReceiptLongIcon sx={{ color: corPrimaria }} />
            Contratação de Legisdots
          </Grid>
          <IconButton onClick={() => setOpenContratar(false)} size="small" disabled={enviando}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ p: isMobile ? 2 : 3 }}>
          {confirmacaoOk ? (
            <Alert severity="success" sx={{ fontFamily: pallet?.general?.fontFamily }}>
              Solicitação registrada. Após a aprovação, os legisdots serão creditados no mês corrente.
            </Alert>
          ) : (
            <>
              <Typography sx={{ fontSize: 13, color: "#555", mb: 2 }}>
                Defina a quantidade de legisdots que deseja contratar.
              </Typography>

              <Grid container justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: "#7a7a7a", textTransform: "uppercase", letterSpacing: 0.4 }}>
                  Quantidade de legisdots
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: corPrimaria }}>
                  {quantidade.toLocaleString("pt-BR")}
                </Typography>
              </Grid>

              <Slider
                value={quantidade}
                onChange={(_, v) => setQuantidade(Array.isArray(v) ? v[0] : v)}
                min={1}
                max={sliderMax}
                step={1}
                valueLabelDisplay="auto"
                valueLabelFormat={(v) => v.toLocaleString("pt-BR")}
                sx={{ color: corPrimaria, mb: 2 }}
              />

              {/* Ticket / recibo simples da contratação */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderStyle: "dashed",
                  background: pallet?.backgroundColorTertiary || "#F9F9F9",
                  borderColor: pallet?.backgroungColorTertiaryBorder || "#cccccc",
                }}
              >
                <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: corTexto,
                      textTransform: "uppercase",
                      letterSpacing: 0.6,
                    }}
                  >
                    Informe de contratação
                  </Typography>
                  <ReceiptLongIcon sx={{ color: corPrimaria, fontSize: 18 }} />
                </Grid>

                <Divider sx={{ borderStyle: "dashed", mb: 1 }} />

                <Grid container justifyContent="space-between" sx={{ py: 0.3 }}>
                  <Typography sx={{ fontSize: 12, color: "#777" }}>Data</Typography>
                  <Typography sx={{ fontSize: 13, color: corTexto }}>
                    {dataContratacao.toLocaleDateString("pt-BR")}
                  </Typography>
                </Grid>

                <Grid container justifyContent="space-between" sx={{ py: 0.3 }}>
                  <Typography sx={{ fontSize: 12, color: "#777" }}>Hora</Typography>
                  <Typography sx={{ fontSize: 13, color: corTexto }}>
                    {dataContratacao.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                  </Typography>
                </Grid>

                <Grid container justifyContent="space-between" sx={{ py: 0.3 }}>
                  <Typography sx={{ fontSize: 12, color: "#777" }}>Item</Typography>
                  <Typography sx={{ fontSize: 13, color: corTexto }}>Legisdots</Typography>
                </Grid>

                <Divider sx={{ borderStyle: "dashed", my: 1 }} />

                <Grid container justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: 13, color: "#444", fontWeight: 700 }}>
                    Total contratado
                  </Typography>
                  <Typography sx={{ fontSize: 15, color: corPrimaria, fontWeight: 700 }}>
                    {quantidade.toLocaleString("pt-BR")} legisdots
                  </Typography>
                </Grid>
              </Paper>

              <FormControlLabel
                sx={{ alignItems: "flex-start", mt: 2 }}
                control={
                  <Checkbox
                    checked={aceite}
                    onChange={(e) => setAceite(e.target.checked)}
                    sx={{ pt: 0.5, color: corPrimaria, "&.Mui-checked": { color: corPrimaria } }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 12, color: "#444", lineHeight: 1.5 }}>
                    Confirmo que sou o administrador responsável previamente designado pela minha
                    organização na plataforma Legisnote e, nesta condição, autorizo a contratação
                    dos legisdots indicados acima. Declaro ciência de que esta contratação será
                    incorporada à próxima fatura de mensalidade da plataforma e reconheço que é
                    irrevogável após a confirmação.
                  </Typography>
                }
              />
            </>
          )}
        </DialogContent>

        {!confirmacaoOk && (
          <DialogActions
            sx={{ background: pallet?.backgroundColorTertiary || "#F9F9F9", px: 3, py: 2 }}
          >
            <Button
              onClick={() => setOpenContratar(false)}
              disabled={enviando}
              sx={{
                textTransform: "none",
                color: corTexto,
                fontFamily: pallet?.general?.fontFamily,
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              disabled={!aceite || quantidade <= 0 || enviando}
              onClick={handleConfirmarContratacao}
              sx={{
                textTransform: "none",
                background: corPrimaria,
                fontFamily: pallet?.general?.fontFamily,
                fontWeight: 600,
                "&:hover": { background: pallet?.textColorTertiary || "#4B1196" },
              }}
            >
              {enviando ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Confirmar contratação"}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Container>
  );
};

export default GerUsuariosTabConsumoIa;
